/**
 * Turning a rep's forecast movement into a bridge (journey J4).
 *
 * The claim the manager's view makes is that any movement is exactly three
 * things: the nightly calibration re-reading history, the world changing under
 * it, and named people pulling levers. This module builds the geometry for that
 * claim, and `movementCloses` is the check that it holds — a decomposition
 * whose parts do not sum to the whole is worse than no decomposition, because
 * it invites the archaeology it exists to prevent.
 *
 * Deliberately its own step shape rather than reusing
 * `insights-revenue-bridge`'s: that module's `WaterfallStep` keys are its own
 * cohort names, and widening them to carry a second, unrelated vocabulary would
 * couple two features that have nothing to say to each other. The geometry is
 * six lines; the coupling would be permanent.
 */

import type {
  InterventionDaySummary,
  InterventionLogEntry,
  MovementCause,
  RepMovement,
} from "../data/my-pipeline-types";
import { formatCurrencyGbpCompact } from "./formatters";
import { MATERIAL_MOVE_PCT } from "./my-pipeline-interventions";

/** One bar of the movement bridge. */
export interface MovementStep {
  key: "opening" | MovementCause | "closing";
  label: string;
  kind: "anchor" | "delta";
  /** Signed: the total for anchors, the movement for deltas. */
  value: number;
  /** Floating-bar bottom. */
  base: number;
  /** Floating-bar top. */
  top: number;
  /**
   * The text written on the bar: unsigned for anchors, signed for deltas.
   *
   * Formatted here rather than in the chart because the chart's label renderer
   * only reliably receives the datum, not its position — deriving anchor-vs-delta
   * from a bar index silently mis-signs the closing anchor as soon as one bar
   * has zero height, which is exactly the common case of a rep who intervened
   * in nothing.
   */
  display: string;
}

const CAUSE_ORDER: MovementCause[] = ["recalibration", "data", "intervention"];

export const CAUSE_LABEL: Record<MovementCause, string> = {
  recalibration: "System recalibrated",
  data: "Data changed",
  intervention: "People intervened",
};

/**
 * What each cause means, in the manager's terms rather than the system's.
 * Kept next to the labels because the two are read together and drift apart
 * the moment they live in different files.
 */
export const CAUSE_MEANING: Record<MovementCause, string> = {
  recalibration:
    "The nightly calibration re-read the win history and changed its own weightings. Nobody did anything.",
  data: "Quotes were raised, orders placed, work despatched. The world moved.",
  intervention:
    "Named people changed something on purpose. Every one is in the log, with a reason.",
};

function signed(value: number): string {
  if (value === 0) return "\u2014";
  return `${value > 0 ? "+" : "\u2212"}${formatCurrencyGbpCompact(Math.abs(value))}`;
}

function valueFor(rep: RepMovement, cause: MovementCause): number {
  if (cause === "recalibration") return rep.recalibration;
  if (cause === "data") return rep.data_change;
  return rep.intervention;
}

/**
 * Build the five bars: opening anchor, the three causes, closing anchor.
 * The running total after the three deltas equals the closing anchor by
 * construction — see `movementCloses`.
 */
export function toMovementSteps(rep: RepMovement): MovementStep[] {
  const steps: MovementStep[] = [
    {
      key: "opening",
      label: "Opening",
      kind: "anchor",
      value: rep.opening,
      base: 0,
      top: rep.opening,
      display: formatCurrencyGbpCompact(rep.opening),
    },
  ];

  let running = rep.opening;
  for (const cause of CAUSE_ORDER) {
    const value = valueFor(rep, cause);
    const from = running;
    const to = running + value;
    running = to;
    steps.push({
      key: cause,
      label: CAUSE_LABEL[cause],
      kind: "delta",
      value,
      base: Math.min(from, to),
      top: Math.max(from, to),
      display: signed(value),
    });
  }

  steps.push({
    key: "closing",
    label: "Now",
    kind: "anchor",
    value: rep.closing,
    base: 0,
    top: rep.closing,
    display: formatCurrencyGbpCompact(rep.closing),
  });

  return steps;
}

/**
 * True when the three causes account for the whole movement.
 *
 * A pound the decomposition cannot explain is the one a manager will chase, so
 * this is asserted in tests rather than trusted. Tolerance is a pound, for
 * rounding at the seams.
 */
export function movementCloses(rep: RepMovement): boolean {
  const parts = rep.recalibration + rep.data_change + rep.intervention;
  return (
    Math.abs(parts - rep.delta) <= 1 &&
    Math.abs(rep.opening + rep.delta - rep.closing) <= 1
  );
}

/** The share of a rep's movement that a human caused, 0–1. */
export function humanShare(rep: RepMovement): number {
  const total =
    Math.abs(rep.recalibration) +
    Math.abs(rep.data_change) +
    Math.abs(rep.intervention);
  return total > 0 ? Math.abs(rep.intervention) / total : 0;
}

/**
 * Net each rep's day of interventions and decide whether it was material.
 *
 * The alert this drives fires **once per rep per day**, not once per lever
 * pull. Two reasons, and both are about what a manager can act on. A rep
 * working through their book pulls several levers in one sitting, and being
 * told about each one separately is noise that trains people to ignore the
 * bell — the thing worth knowing is where their number ended up. And a rep who
 * raises one quote and drops another by the same amount has not moved the
 * forecast at all; only netting says so.
 *
 * The denominator is the rep's whole projected unbooked figure, because that is
 * the number the swing is a swing *in*. A £40k change means something quite
 * different against a £120k book and a £900k one.
 *
 * `unbookedByRep` is the rep's projected unbooked for the month. A rep missing
 * from it has no denominator, so their day cannot be material — that is
 * deliberately not the same as "0% and therefore fine", but the alternative
 * (dividing by zero, or by a guessed figure) is worse.
 *
 * `openDate` is the day still in progress, whose digest has not fired yet. A
 * material day is only `alerted` once it has closed, because the afternoon can
 * net the morning back out.
 */
export function summariseInterventionDays(
  entries: readonly InterventionLogEntry[],
  unbookedByRep: Readonly<Record<string, number>>,
  openDate?: string,
): InterventionDaySummary[] {
  const byRepDay = new Map<string, InterventionDaySummary>();

  for (const entry of entries) {
    const date = entry.at.slice(0, 10);
    const key = `${date}|${entry.rep_id}`;
    const unbooked = unbookedByRep[entry.rep_id] ?? 0;
    const existing = byRepDay.get(key);
    if (existing) {
      existing.entry_count += 1;
      existing.net_delta += entry.month_delta;
      continue;
    }
    byRepDay.set(key, {
      date,
      rep_id: entry.rep_id,
      rep_name: entry.rep_name,
      entry_count: 1,
      net_delta: entry.month_delta,
      projected_unbooked: unbooked,
      share: 0,
      material: false,
      alerted: false,
    });
  }

  const days = [...byRepDay.values()];
  for (const day of days) {
    day.share =
      day.projected_unbooked > 0
        ? Math.abs(day.net_delta) / day.projected_unbooked
        : 0;
    day.material = day.share >= MATERIAL_MOVE_PCT;
    day.alerted = day.material && day.date !== openDate;
  }

  // Newest first, and stable within a day so an all-reps view does not
  // reshuffle between loads.
  days.sort(
    (a, b) =>
      b.date.localeCompare(a.date) || a.rep_name.localeCompare(b.rep_name),
  );
  return days;
}
